import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Megaphone, Send, Search } from "lucide-react";
import { api, CHANNEL_COLORS, resolveImageUrl } from "@/lib/api";
import { BROADCAST } from "@/constants/testIds";
import { toast } from "sonner";

export default function BroadcastDialog({ product, onSent }) {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/broadcast/candidates")
      .then(({ data }) => setCandidates(data || []))
      .catch(() => toast.error("Failed to load contacts"))
      .finally(() => setLoading(false));
    if (product) {
      setCaption(
        `✨ New arrival: ${product.name}\n` +
          `${product.fabric} · ₹${Number(product.price).toLocaleString("en-IN")}\n` +
          `Reply to know more.`,
      );
    }
    setSelected(new Set());
  }, [open, product]);

  if (!product) return null;

  const filtered = candidates.filter((c) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (c.customer_name || "").toLowerCase().includes(s) ||
      (c.phone || "").toLowerCase().includes(s) ||
      (c.channel || "").toLowerCase().includes(s)
    );
  });

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.latest_inquiry_id)));
  };

  const send = async () => {
    if (selected.size === 0) {
      toast.error("Pick at least one recipient");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/broadcast", {
        product_id: product.id,
        recipient_inquiry_ids: [...selected],
        caption,
      });
      toast.success(
        `Broadcasting to ${data.recipients} contact${data.recipients === 1 ? "" : "s"}…`,
      );
      setOpen(false);
      onSent?.();
    } catch {
      toast.error("Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid={BROADCAST.openButton(product.id)}
          className="text-stone-500 hover:text-terracotta transition-colors"
          title="Broadcast to past buyers"
          onClick={(e) => e.stopPropagation()}
        >
          <Megaphone className="w-3.5 h-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid={BROADCAST.dialog}
        className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Broadcast this saree
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Send the same saree card to a hand-picked list of past ManyChat contacts.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-4 items-start">
          <img
            src={resolveImageUrl(product.photo_url)}
            alt=""
            className="w-24 h-32 rounded-xl object-cover border border-stone-200 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg text-stone-900 truncate">
              {product.name}
            </div>
            <div className="text-xs text-stone-500">
              {product.fabric} · ₹
              {Number(product.price).toLocaleString("en-IN")}
            </div>
            <Textarea
              data-testid={BROADCAST.captionInput}
              className="mt-2 text-sm"
              rows={3}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="overline">Past ManyChat contacts</div>
            <div className="text-xs text-stone-400">
              ({candidates.length} total)
            </div>
            <div className="flex-1" />
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                className="pl-8 h-8 w-48"
                placeholder="Search name/phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="border border-stone-200 rounded-xl max-h-64 overflow-y-auto">
            {loading && (
              <div className="text-stone-400 text-sm py-6 text-center">
                Loading…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-stone-400 text-sm py-6 text-center">
                No past ManyChat contacts yet.
              </div>
            )}
            {filtered.length > 0 && (
              <>
                <label className="flex items-center gap-2 px-3 py-2 border-b border-stone-100 bg-stone-50 text-xs text-stone-600 cursor-pointer select-none">
                  <Checkbox
                    data-testid={BROADCAST.selectAll}
                    checked={
                      filtered.length > 0 && selected.size === filtered.length
                    }
                    onCheckedChange={toggleAll}
                  />
                  {selected.size} / {filtered.length} selected
                </label>
                <div className="divide-y divide-stone-100">
                  {filtered.map((c) => (
                    <label
                      key={c.latest_inquiry_id}
                      data-testid={BROADCAST.recipientRow(c.latest_inquiry_id)}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={selected.has(c.latest_inquiry_id)}
                        onCheckedChange={() => toggle(c.latest_inquiry_id)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-stone-800 truncate">
                          {c.customer_name}
                        </div>
                        <div className="text-xs text-stone-500 truncate">
                          {c.phone || "—"} · {c.inquiries_count} past inquiries
                        </div>
                      </div>
                      <span
                        className={`heritage-badge ${
                          CHANNEL_COLORS[c.channel] || ""
                        }`}
                      >
                        {c.channel}
                      </span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            data-testid={BROADCAST.submit}
            disabled={sending || selected.size === 0}
            className="bg-terracotta hover:bg-terracotta-dark"
            onClick={send}
          >
            <Send className="w-4 h-4 mr-2" />
            {sending
              ? "Broadcasting…"
              : `Send to ${selected.size || 0} ${
                  selected.size === 1 ? "contact" : "contacts"
                }`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
